<template>
<div class="ql-page-nav">
    <md-button class="md-icon-button" v-bind:disabled="!canPageDown" v-on:click="pageDown"><md-icon>keyboard_arrow_left</md-icon></md-button>
    <div class="page-number-container">
        <input type="text" v-if="pageNames[page - 1] && pageNames[page - 1].name" @change="setPage" :value="pageNames[page - 1].name" class="current-page-input" /> / {{ pageNames | lastPageName }}
    </div>
    <md-button class="md-icon-button" v-bind:disabled="!canPageUpDisplay" v-on:click="pageUp"><md-icon>keyboard_arrow_right</md-icon></md-button>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import pageTurnFromKey from '@/lib/page-turn-key';
import { pageNamesOr } from '@/lib/page-names';

const ProjectPagination = {
    name: 'projectPagination',
    props: [
        'currentPage',
        'selectedPageRenameIndices',
        // Optional. How many pages an arrow moves. Absent (PDF view) keeps the
        // existing 1-or-2 behaviour, which encodes single/even/odd spreads.
        //
        // The thumbnail grid passes its measured ROW WIDTH: stepping one page in
        // a grid four across barely changes what you see, which is why the
        // arrows felt unresponsive there. Spread logic is meaningless for a
        // grid, so a step bypasses it entirely.
        'step'
    ],
    data: function() {
        return {};
    },
    watch: {
        totalPages: {
            handler(newValue) {
                if (this.page > newValue) {
                    this.$emit('setPage', 1);
                }
            },
            immediate: true
        }
    },
    computed: {
        // A usable step, or null to mean "behave as before". Guards against a
        // row width that has not been measured yet, or a nonsense value.
        stepSize() {
            const n = Number(this.step);
            return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
        },
        ...mapGetters([
            'selectedPageType',
            'project',
            'currentVersion',
            'projectTotalPages'
        ]),
        page: {
            set(page) {
                if (page && !Number.isNaN(page)) {
                    this.$emit('setPage', Math.max(Math.min(this.totalPages, page), 1));
                }
            },
            get() {
                return this.currentPage;
            }
        },
        canPageDown() {
            return this.page > 1;
        },
        canPageUp() {
            return this.page < this.totalPages;
        },
        // Right arrow is usable unless we're at the end. Mirrors the old show-logic
        // (which hid the arrow) so it now grays out instead — no layout shift.
        // Spread views (even/odd) treat the last spread (page+1 === last) as the end.
        canPageUpDisplay() {
            if (this.selectedPageType === 'no') {
                return this.canPageUp;
            }
            return this.canPageUp && this.page + 1 !== this.totalPages;
        },
        versionID: function() {
            return this.currentVersion ? this.currentVersion.id : '';
        },
        // One page list for the live show and a saved version. Versions saved by the legacy
        // app can have none (21 Oct 2026): number them 1..N from the loaded PDF.
        pageNames: function() {
            if (this.versionID) {
                return pageNamesOr(this.currentVersion && this.currentVersion.pdfPageNumbers, this.totalPages);
            }
            return (this.project && this.project.pdfPageNumbers) || [];
        },
        totalPages: function() {
            if (this.versionID) {
                return this.projectTotalPages || 0;
            }
            
            return (this.project && Array.isArray(this.project.pdfPageNumbers)) 
                ? this.project.pdfPageNumbers.length 
                : 0;
        }
    },
    methods: {
        pageDown() {
            if (this.canPageDown) {
                if (this.stepSize) {
                    this.$emit('setPage', Math.max(1, this.page - this.stepSize));
                    return;
                }
                if (this.selectedPageType === 'no') {
                    this.$emit('setPage', Math.min(this.totalPages, this.page - 1));
                } else if (this.selectedPageType === 'even') {
                    if (this.totalPages === this.page) {
                        this.$emit('setPage', Math.min(this.totalPages, this.page - 2));
                    } else if (this.page - 1 === 1) {
                        this.$emit('setPage', Math.min(this.totalPages, this.page - 1));
                    } else {
                        this.$emit('setPage', Math.min(this.totalPages, this.page - 2));
                    }
                } else if (this.selectedPageType === 'odd') {
                    if (this.page % 2 === 0) {
                        this.$emit('setPage', Math.min(this.totalPages, this.page - 1));
                    } else {
                        this.$emit('setPage', Math.min(this.totalPages, this.page - 2));
                    }
                }
            }
        },
        pageUp() {
            if (this.canPageUp) {
                if (this.stepSize) {
                    this.$emit('setPage', Math.min(this.totalPages, this.page + this.stepSize));
                    return;
                }
                if (this.selectedPageType === 'no') {
                    this.$emit('setPage', Math.min(this.totalPages, this.page + 1));
                } else if (this.selectedPageType === 'even') {
                    if (this.totalPages === this.page + 2 && this.page !== 1) {
                        this.$emit('setPage', Math.min(this.totalPages, this.page + 2));
                    } else if (this.totalPages === this.page + 2 && this.page === 1) {
                        this.$emit('setPage', Math.min(this.totalPages, this.page + 1));
                    } else if (this.page === 1) {
                        this.$emit('setPage', Math.min(this.totalPages, this.page + 1));
                    } else {
                        this.$emit('setPage', Math.min(this.totalPages, this.page + 2));
                    }
                } else if (this.selectedPageType === 'odd') {
                    if (this.page % 2 === 0) {
                        this.$emit('setPage', Math.min(this.totalPages, this.page + 1));
                    } else {
                        this.$emit('setPage', Math.min(this.totalPages, this.page + 2));
                    }
                }
            }
        },
        setPage(e) {
            const inputValue = e.target.value;
            const pageIndex = this.pageNames.findIndex(item => item.name === inputValue);
            if (pageIndex !== -1) {
                this.page = pageIndex + 1;
            }
        },
        handleKeyDown(e) {
            // Arrows and Page Up/Down page the PDF unless the user is typing in a field.
            const turn = pageTurnFromKey(e);
            if (turn === 'prev') {
                this.pageDown();
                e.preventDefault();
            }
            if (turn === 'next') {
                this.pageUp();
                e.preventDefault();
            }
        }
    },
    components: {
    },
    mounted: function() {
        document.addEventListener('keydown', this.handleKeyDown);
    },
    beforeDestroy: function() {
        document.removeEventListener('keydown', this.handleKeyDown);
    }
};

export default ProjectPagination;
</script>

<style lang="scss">
.ql-page-nav {
    .md-icon-button {
        margin: 0;
    }
    .page-number-container {
        display: inline-block;
        padding: 8px 0;
        font-size: 17px;
        font-weight: 100;
        white-space: nowrap;
        .current-page-input {
            min-width: 50px;
            width: 60px;
            text-align: center;
            font-size: 17px;
            font-weight: 100;
            background: transparent;
            color: white;
            border: solid 1px #aaa;
            border-radius: 4px;
        }
    }
}
</style>


// WEBPACK FOOTER //
// pagination.vue?884509c8